import {
  $create,
  $getDocument,
  $getState,
  $setState,
  createState,
  DecoratorNode,
  type DOMExportOutput,
  type LexicalEditor,
  type LexicalNode,
  type NodeKey,
  type StateConfig,
  type StaticNodeConfigRecord,
} from "lexical";
import {
  ATTACHMENT_NODE_TYPE,
  ATTACHMENT_PAYLOAD_KEY,
  attachmentLabel,
  checkAttachmentRef,
  isImageAttachment,
  type AttachmentRef,
} from "../../core/attachments";
import { CONTENT_CLASSES } from "../../core/content-classes";

/**
 * The whole reference as one value, as a token's payload is. Kept whole so that what is stored is
 * exactly what `checkAttachmentRef` checks: state is left out of the JSON when it equals its
 * default, and an empty `alt` would disappear if it were a field of its own.
 */
export const attachmentState: StateConfig<typeof ATTACHMENT_PAYLOAD_KEY, AttachmentRef | null> =
  createState(ATTACHMENT_PAYLOAD_KEY, {
    parse: (value) => (checkAttachmentRef(value) === null ? (value as AttachmentRef) : null),
  });

/**
 * Draws an attachment. What it returns is handed to whatever mounts the editor's decorators, so
 * for `<Editor>` it is a React element; this module knows nothing of React, because a server that
 * seeds a shared document builds the same node with nothing to draw.
 */
export type AttachmentRender = (nodeKey: NodeKey, attachment: AttachmentRef) => unknown;

/** Set by the attachments extension, so a node can find its editor's renderer. */
export const renderers: WeakMap<LexicalEditor, AttachmentRender> = new WeakMap();

type AttachmentNodeConfig = StaticNodeConfigRecord<
  typeof ATTACHMENT_NODE_TYPE,
  {
    readonly extends: typeof DecoratorNode;
    readonly stateConfigs: readonly [
      { readonly flat: true; readonly stateConfig: typeof attachmentState },
    ];
  }
>;

/**
 * An image or a file, as a block of its own. It holds a reference and nothing else: no bytes, no
 * URL, and nothing about an upload, so none of that is ever saved, undone, or sent to another
 * writer. What is drawn inside it comes from the host's adapter, through the renderer.
 */
export class AttachmentNode extends DecoratorNode<unknown> {
  override $config(): AttachmentNodeConfig {
    return this.config(ATTACHMENT_NODE_TYPE, {
      extends: DecoratorNode,
      stateConfigs: [{ flat: true, stateConfig: attachmentState }],
    });
  }

  getAttachment(): AttachmentRef | null {
    return $getState(this, attachmentState);
  }

  setAttachment(attachment: AttachmentRef): this {
    return $setState(this, attachmentState, attachment);
  }

  override createDOM(): HTMLElement {
    const dom = $getDocument().createElement("figure");
    this.describe(dom);
    return dom;
  }

  override updateDOM(_prevNode: this, dom: HTMLElement): boolean {
    this.describe(dom);
    return false;
  }

  /** The space an image will take is kept from the start, so nothing moves when it arrives. */
  private describe(dom: HTMLElement): void {
    const attachment = this.getAttachment();
    const image = attachment !== null && isImageAttachment(attachment);
    // One class at a time, not `className`: the extension marks a selected attachment with a class
    // of its own on this element.
    dom.classList.add(CONTENT_CLASSES.attachment);
    dom.classList.toggle(CONTENT_CLASSES.attachmentImage, image);
    dom.classList.toggle(CONTENT_CLASSES.attachmentFile, !image);
    if (image && attachment.width !== undefined && attachment.height !== undefined) {
      dom.style.setProperty("--ze-attachment-width", `${attachment.width}px`);
      dom.style.setProperty("--ze-attachment-aspect", `${attachment.width} / ${attachment.height}`);
    } else {
      dom.style.removeProperty("--ze-attachment-width");
      dom.style.removeProperty("--ze-attachment-aspect");
    }
  }

  override decorate(editor: LexicalEditor): unknown {
    const attachment = this.getAttachment();
    const render = renderers.get(editor);
    return attachment === null || render === undefined ? null : render(this.getKey(), attachment);
  }

  /** What another application is given: the name, which is all that means anything there. */
  override exportDOM(): DOMExportOutput {
    const element = $getDocument().createElement("p");
    element.textContent = this.getTextContent();
    return { element };
  }

  override getTextContent(): string {
    const attachment = this.getAttachment();
    return attachment === null ? "" : attachmentLabel(attachment);
  }

  override isInline(): false {
    return false;
  }
}

export function $createAttachmentNode(attachment: AttachmentRef): AttachmentNode {
  return $create(AttachmentNode).setAttachment(attachment);
}

export const $isAttachmentNode = (node: LexicalNode | null | undefined): node is AttachmentNode =>
  node instanceof AttachmentNode;
